class GiftCardRecipientForm extends HTMLElement {
  constructor() {
    super();

    this.checkbox = this.querySelector('[data-recipient-checkbox]');
    this.formFields = this.querySelector('[data-recipient-fields]');
    this.emailInput = this.querySelector('[data-recipient-email-input]');
    this.emailError = this.querySelector('[data-recipient-email-error]');
    this.messageInput = this.querySelector('[data-recipient-message-input]');
    this.charCountCurrent = this.querySelector('[data-recipient-char-current]');
    this.charCountMax = this.querySelector('[data-recipient-char-max]');
    this.charCountContainer = this.querySelector('[data-recipient-char-count]');
    this.hiddenInput = this.formFields ? this.formFields.querySelector('input[type="hidden"][name*="__shopify_send_gift_card_to_recipient"]') : null;

    this.maxLength = this.messageInput ? parseInt(this.messageInput.getAttribute('maxlength')) || 200 : 200;

    this.originalEmailName = this.emailInput ? this.emailInput.getAttribute('name') : null;
    this.originalMessageName = this.messageInput ? this.messageInput.getAttribute('name') : null;
    this.originalHiddenInputName = this.hiddenInput ? this.hiddenInput.getAttribute('name') : null;

    this.isHiding = false;
  }

  connectedCallback() {
    if (this.formFields && this.formFields.classList.contains('hidden')) {
      this.removeFormFieldNames();
    }

    if (this.checkbox) {
      this.checkbox.addEventListener('sl-change', this.handleCheckboxChange.bind(this));
    }

    if (this.formFields) {
      this.formFields.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
    }

    if (this.emailInput) {
      this.emailInput.addEventListener('sl-input', this.handleEmailInput.bind(this));
      this.emailInput.addEventListener('sl-blur', this.handleEmailBlur.bind(this));
      this.updateFilledClass(this.emailInput);
    }

    if (this.messageInput) {
      this.messageInput.addEventListener('sl-input', this.updateCharacterCount.bind(this));
      this.updateCharacterCount();
    }

    const form = this.closest('form');
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.checkbox) {
      this.checkbox.removeEventListener('sl-change', this.handleCheckboxChange.bind(this));
    }

    if (this.formFields) {
      this.formFields.removeEventListener('transitionend', this.handleTransitionEnd.bind(this));
    }

    if (this.emailInput) {
      this.emailInput.removeEventListener('sl-input', this.handleEmailInput.bind(this));
      this.emailInput.removeEventListener('sl-blur', this.handleEmailBlur.bind(this));
    }

    if (this.messageInput) {
      this.messageInput.removeEventListener('sl-input', this.updateCharacterCount.bind(this));
    }

    const form = this.closest('form');
    if (form) {
      form.removeEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }

  handleCheckboxChange(event) {
    const isChecked = event.target.checked;

    if (isChecked) {
      this.showForm();
    } else {
      this.hideForm();
    }
  }

  updateFilledClass(input) {
    if (input && input.value && input.value.trim()) {
      input.classList.add('filled');
    } else if (input) {
      input.classList.remove('filled');
    }
  }

  handleEmailInput() {
    this.updateFilledClass(this.emailInput);
    this.clearEmailError();
  }

  handleEmailBlur(event) {
    this.updateFilledClass(this.emailInput);
    this.validateEmail(event);
  }

  showForm() {
    this.formFields.classList.remove('hidden');
    requestAnimationFrame(() => {
      this.formFields.classList.add('visible');
    });

    if (this.emailInput && this.originalEmailName) {
      this.emailInput.setAttribute('name', this.originalEmailName);
      this.emailInput.setAttribute('required', 'required');
    }

    if (this.messageInput && this.originalMessageName) {
      this.messageInput.setAttribute('name', this.originalMessageName);
    }

    if (this.hiddenInput && this.originalHiddenInputName) {
      this.hiddenInput.setAttribute('name', this.originalHiddenInputName);
    }
  }

  removeFormFieldNames() {
    if (this.emailInput) {
      this.emailInput.removeAttribute('name');
      this.emailInput.removeAttribute('required');
    }

    if (this.messageInput) {
      this.messageInput.removeAttribute('name');
    }

    if (this.hiddenInput) {
      this.hiddenInput.removeAttribute('name');
    }
  }

  handleTransitionEnd(event) {
    if (this.emailInput) {
      this.emailInput.value = '';
      this.emailInput.removeAttribute('data-user-invalid');
      this.clearEmailError();
      this.updateFilledClass(this.emailInput);
    }

    if (!this.isHiding) return;
    if (event.target !== this.formFields) return;
    if (event.propertyName !== 'max-height') return;

    this.formFields.classList.add('hidden');
    this.isHiding = false;

    this.removeFormFieldNames();

    if (this.messageInput) {
      this.messageInput.value = '';
      this.updateCharacterCount();
    }
  }

  hideForm() {
    this.isHiding = true;
    this.formFields.classList.remove('visible');
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  validateEmail(event) {
    if (this.formFields && (this.formFields.classList.contains('hidden') || !this.formFields.classList.contains('visible'))) {
      return true;
    }

    const email = this.emailInput.value.trim();

    const isValid = this.emailInput.checkValidity();

    if (!isValid) {
      const errorMessage = !email
        ? 'Please enter an email address'
        : 'Please enter a valid email address';

      this.showEmailError();
      this.emailInput.setCustomValidity(errorMessage);
      this.emailInput.setAttribute('data-user-invalid', '');
      return false;
    } else {
      this.clearEmailError();
      this.emailInput.setCustomValidity('');
      this.emailInput.removeAttribute('data-user-invalid');
      return true;
    }
  }

  showEmailError() {
    if (this.emailError) {
      this.emailError.classList.remove('hidden');
    }
  }

  clearEmailError() {
    if (this.emailError) {
      this.emailError.classList.add('hidden');
    }
    if (this.emailInput) {
      this.emailInput.setCustomValidity('');
      this.emailInput.removeAttribute('data-user-invalid');
    }
  }

  updateCharacterCount() {
    if (!this.messageInput || !this.charCountCurrent) return;

    const currentLength = this.messageInput.value.length;
    this.charCountCurrent.textContent = currentLength;

    if (currentLength > this.maxLength * 0.9) {
      this.charCountContainer.classList.remove('c-grey-400');
      this.charCountContainer.classList.add('c-grey-600');
    } else {
      this.charCountContainer.classList.remove('c-grey-600');
      this.charCountContainer.classList.add('c-grey-400');
    }
  }

  handleFormSubmit(event) {
    if (this.formFields && !this.formFields.classList.contains('hidden') && this.formFields.classList.contains('visible')) {
      const email = this.emailInput.value.trim();

      const isValid = this.emailInput.checkValidity();

      if (!isValid) {
        event.preventDefault();
        const errorMessage = !email
          ? 'Please enter an email address'
          : 'Please enter a valid email address';
        this.showEmailError();
        this.emailInput.setCustomValidity(errorMessage);
        this.emailInput.setAttribute('data-user-invalid', '');
        this.emailInput.focus();
        return false;
      }
    }

    return true;
  }
}

if (typeof customElements.get('gift-card-recipient-form') === 'undefined') {
  customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
}
